"use client"

import { useState, useEffect } from "react"
import { useApp } from "../../contexts/AppContext"
import { BiPencil, BiTrash } from "react-icons/bi"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"

import AnimatedLoader from "../ui/AnimatedLoader"
import ConfirmAlert from "../alerts/ConfirmAlert"
import TableLayout from "../tables/TableLayout"

export default function FeuilleDataTable({ feuilleId, classeurId, feuilleData, dataInput, setDataInput }) {
  const navigate = useNavigate()
  const { getFeuille, getFeuilleLignes, loading } = useApp()
  const [selectedItem, setSelectedItem] = useState([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [feuilleColumns, setFeuilleColumns] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const currentFeuille = getFeuille(feuilleId)

  useEffect(() => {
    const columns = getFeuilleLignes(feuilleId)
    setFeuilleColumns(columns)
  }, [feuilleId, dataInput])

  // --- SINGLE DELETE ---
  const confirmDeleteData = (key) => {
    setDeleteTarget(key)
    setShowDeleteConfirm(true)
  }

  const handleDeleteData = () => {
    if (deleteTarget) {
      const updated = dataInput.filter(item => item.key_data !== deleteTarget)
      setDataInput(updated)
      setDeleteTarget(null)
    }
    setShowDeleteConfirm(false)
  }

  // --- MULTI DELETE ---
  const handleMultiDeleteData = () => {
    if (selectedItem.length > 0) {
      const selectedIds = selectedItem.map(item => item.key_data)
      const updated = dataInput.filter(item => !selectedIds.includes(item.key_data))
      // setFeuilleData(updated)
      setDataInput(updated)
      setSelectedItem([])
    }
  }

  // --- COLUMNS ---
  const columnsTable = [
    {
      name: 'N°',
      selector: (_, index) => index + 1,
      width: '60px',
    },
    ...feuilleColumns.map(col => ({
      name: col.nom_ligne,
      selector: row => row[col.nom_ligne] || '-',
      sortable: true,
      wrap: true,
    })),
    {
      name: 'Actions',
      cell: row => (
        <>
            <button
              className="btn btn-sm btn-outline-light text-danger"
              onClick={() => confirmDeleteData(row.key_data)}
              title="Supprimer"
              type="button"
              disabled={!row.key_data && row.key_data !== null}
            >
              <BiTrash />
            </button>
        </>
      ),
      ignoreRowClick: true,
      allowoverflow: true,
      button: "true",
    },
  ]

  // --- ANIMATIONS ---
  const tableVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.05 } },
  }

  // --- LOADING ---
  if (loading && feuilleData.length === 0) {
    return <AnimatedLoader text="Chargement des données..." />
  }

  // --- EMPTY DATA ---
  if (feuilleColumns.length === 0) {
    return (
      <div className="text-center py-5 bg-light border rounded">
        <p className="text-muted m-0">
          Aucun champ défini. <br />
          Configurez d'abord les champs de cette feuille.
        </p>
      </div>
    )
  }

  return (
    <>
      <motion.div
        className="table-responsive"
        variants={tableVariants}
        initial="hidden"
        animate="visible"
      >
        <TableLayout
          title={`Données - ${currentFeuille?.nom_feuille}`}
          columns={columnsTable}
          data={feuilleData}
          setSelectedItem={setSelectedItem}
          handleDeleteData={handleMultiDeleteData}
          loading={loading}
        />
      </motion.div>

      {/* CONFIRM DELETE */}
      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteData}
        title="Supprimer la ligne de données"
        message="Êtes-vous sûr de vouloir supprimer cette ligne ? Cette action est irréversible."
        confirmText="Supprimer"
        variant="danger"
      />
    </>
  )
}
